/**
 * Google-klik-ID vastleggen en bewaren, zodat een bestelling later aan de advertentieklik
 * gekoppeld kan worden.
 *
 * Waarom dit bestaat: de aankoop-conversie vuurt in de browser, op de bedankpagina ná Mollie.
 * Komt de klant daar nooit — betalen in de bankapp en de tab wegklikken — dan ziet Google Ads
 * die bestelling nooit, hoe goed de tag ook staat. De server wéét wel dat er betaald is. Door
 * het klik-ID bij de order te bewaren kunnen we die bestellingen achteraf als offline conversie
 * bij Google Ads importeren (zie `src/features/admin/ads-conversions.ts`).
 *
 * Drie soorten klik-ID, afhankelijk van het kanaal:
 *  - `gclid`  — de gewone Google Ads-klik (zoeken, display, shopping);
 *  - `gbraid` — iOS-app-verkeer zonder IDFA;
 *  - `wbraid` — web-verkeer vanuit een app, ook zonder IDFA.
 * Google accepteert ze alle drie bij het importeren, elk in een eigen kolom.
 *
 * Toestemming: een klik-ID is advertentiemeting, dus we bewaren het alleen met
 * marketing-toestemming — dezelfde grens die Consent Mode aan `ad_storage` hangt. Zonder
 * toestemming laten we het staan en gebeurt er niets; de bestelling gaat dan gewoon zonder
 * klik-ID naar de server.
 */
import { consentSnapshot } from "./consent";

const KEY = "gt_click_id";
/** Google's conversieperiode is maximaal 90 dagen; daarna is het ID waardeloos. */
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

export type ClickIdType = "gclid" | "gbraid" | "wbraid";

export interface StoredClickId {
  type: ClickIdType;
  value: string;
  /** Tijdstip van de klik (ms sinds epoch) — bepaalt of het ID nog binnen de 90 dagen valt. */
  ts: number;
}

/** Een klik-ID is een ondoorzichtige tekenreeks van Google; alleen lengte/tekens bewaken. */
const GELDIG = /^[A-Za-z0-9._-]{10,200}$/;

/** Leest het klik-ID uit een URL-querystring. Eerst gclid, dan de app-varianten. */
export function clickIdFromSearch(search: string): { type: ClickIdType; value: string } | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }
  for (const type of ["gclid", "gbraid", "wbraid"] as const) {
    const value = params.get(type);
    if (value && GELDIG.test(value)) return { type, value };
  }
  return null;
}

/** Is dit bewaarde ID nog bruikbaar (niet verlopen)? */
export function clickIdGeldig(stored: StoredClickId | null, nu: number): stored is StoredClickId {
  return !!stored && nu - stored.ts < MAX_AGE_MS && GELDIG.test(stored.value);
}

/**
 * Legt het klik-ID uit de huidige URL vast. Een nieuwe klik overschrijft de oude: Google rekent
 * de conversie toe aan de láátste klik, dus dat is ook het ID dat we willen bewaren.
 * Roep dit aan bij elke navigatie — het is een paar microseconden en de klant kan via een
 * advertentie op een willekeurige pagina binnenkomen.
 */
export function captureClickId(search: string, nu: number = Date.now()): void {
  const hit = clickIdFromSearch(search);
  if (!hit) return;
  if (!consentSnapshot()?.marketing) return; // geen marketing-toestemming → niets bewaren
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...hit, ts: nu } satisfies StoredClickId));
  } catch {
    /* geen storage (privémodus) → dan simpelweg geen offline conversie voor dit bezoek */
  }
}

/** Het bewaarde klik-ID, of null als het er niet is, verlopen is of onleesbaar. */
export function readClickId(nu: number = Date.now()): StoredClickId | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredClickId;
    if (!clickIdGeldig(parsed, nu)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Wist het bewaarde klik-ID (gebruikt als de klant marketing-toestemming intrekt). */
export function clearClickId(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* niets te wissen */
  }
}
